import { CURRENCIES } from "@/constants/currency";

/**
 * Para birimi formatlaması yapan yardımcı fonksiyon
 * @param {number} amount - Formatlanacak tutar
 * @param {string} currencyCode - Para birimi ISO kodu (TRY, USD, EUR, SAR)
 * @param {Object} options - Formatlama seçenekleri
 * @param {boolean} options.showSymbol - Sembol gösterilsin mi? (varsayılan: true)
 * @param {boolean} options.showCode - ISO kodu gösterilsin mi? (varsayılan: false)
 * @param {string} options.symbolPosition - Sembol pozisyonu: 'left' | 'right' (varsayılan: 'left')
 * @param {number} options.decimals - Ondalık basamak sayısı (varsayılan: 2)
 * @param {boolean} options.round - Yuvarlama yapılsın mı? (varsayılan: true)
 * @param {string} options.locale - Locale formatlaması (varsayılan: 'tr-TR')
 * @returns {string} Formatlanmış para birimi metni
 */
export function formatCurrency(amount, currencyCode, options = {}) {
  const {
    showSymbol = false,
    showCode = true,
    symbolPosition = "right",
    decimals = 0,
    round = true,
    locale = "tr-TR",
  } = options;

  // Geçerli para birimi bilgisini al
  const safeCurrencyCode = currencyCode || "TRY";
  const currencyInfo =
    CURRENCIES[
      safeCurrencyCode.toLocaleLowerCase() == "tl" ? "TRY" : safeCurrencyCode
    ];

  if (!currencyInfo) {
    console.warn(`Geçersiz para birimi: ${currencyCode}`);
    return amount.toString();
  }

  // Tutarı işle
  let processedAmount = amount;

  // Yuvarlama uygula
  if (round) {
    processedAmount =
      Math.round(processedAmount * Math.pow(10, decimals)) /
      Math.pow(10, decimals);
  }

  // Tutarı formatla
  const formattedAmount = processedAmount.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  // Sembol veya kod gösterimini belirle
  let displaySymbol = "";
  if (showCode) {
    displaySymbol = currencyInfo.code;
  } else if (showSymbol) {
    displaySymbol = currencyInfo.symbol;
  }

  // Pozisyona göre formatla
  if (!displaySymbol) {
    return formattedAmount;
  }

  if (symbolPosition === "right" || showCode) {
    return `${formattedAmount} ${displaySymbol}`;
  } else {
    return `${displaySymbol}${formattedAmount}`;
  }
}

/**
 * Yaygın kullanılan formatlar için hazır fonksiyonlar
 */
export const currencyFormats = {
  /**
   * Standart format: ₺1.234,50
   */
  standard: (amount, currencyCode) => formatCurrency(amount, currencyCode),

  /**
   * Kod ile format: 1.234,50 TRY
   */
  withCode: (amount, currencyCode) =>
    formatCurrency(amount, currencyCode, {
      showSymbol: false,
      showCode: true,
    }),

  /**
   * Tam sayı format: ₺1.234
   */
  integer: (amount, currencyCode) =>
    formatCurrency(amount, currencyCode, {
      decimals: 0,
    }),

  /**
   * Yuvarlama olmadan: ₺1.234,567
   */
  precise: (amount, currencyCode, decimals = 3) =>
    formatCurrency(amount, currencyCode, {
      round: false,
      decimals,
    }),
};
